import styled from 'styled-components';

import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;
  min-height: 100%;

  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @media (max-width: 1199px) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.01em;
  margin-bottom: 4px;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.onSurfaceVariant};
`;

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1199px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
`;

export const FilterTab = styled.button<{ active: boolean }>`
  padding: 8px 16px;

  background-color: ${props => (props.active ? colors.primary : 'transparent')};
  border: 1px solid
    ${props => (props.active ? colors.primary : colors.outlineVariant)};
  border-radius: ${radius.full};
  cursor: pointer;

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props =>
    props.active ? colors.onPrimary : colors.onSurfaceVariant};

  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
`;

export const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const MessageCard = styled.div`
  padding: 20px;
  border-radius: ${radius.md};

  background-color: ${colors.surfaceContainerLow};
  border: 1px solid ${colors.outlineVariant}33;
`;

export const MessageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 1199px) {
    flex-direction: column;
  }
`;

export const MessageSubject = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.onSurface};
`;

export const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;

  span {
    font-size: 12px;
    color: ${colors.onSurfaceVariant};
  }
`;

export const Dot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: ${radius.full};
  background-color: ${colors.onSurfaceVariant};
`;

export const OriginBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  padding: 2px 8px;
  border-radius: ${radius.full};
  background-color: ${colors.surfaceContainer};

  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.onSurfaceVariant};
`;

type StatusAccent = 'open' | 'answered' | 'closed';

const statusColor: Record<StatusAccent, string> = {
  open: colors.primary,
  answered: colors.tertiary,
  closed: colors.onSurfaceVariant,
};

export const StatusBadge = styled.span<{ status: StatusAccent }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 4px 10px;
  border-radius: ${radius.full};
  background-color: ${props => statusColor[props.status]}1f;

  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => statusColor[props.status]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${radius.full};
    background-color: ${props => statusColor[props.status]};
  }
`;

export const MessageBody = styled.p`
  font-size: 13.5px;
  line-height: 1.6;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 16px;

  white-space: pre-wrap;
`;

export const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  padding-top: 14px;
  border-top: 1px solid ${colors.outlineVariant}26;
`;

export const StatusButton = styled.button<{ active: boolean }>`
  padding: 6px 14px;

  background-color: ${props =>
    props.active ? colors.surfaceContainerHighest : 'transparent'};
  border: 1px solid ${colors.outlineVariant}80;
  border-radius: ${radius.full};
  cursor: pointer;

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${props =>
    props.active ? colors.onSurface : colors.onSurfaceVariant};

  &:hover {
    background-color: ${colors.surfaceContainer};
  }

  &:disabled {
    cursor: default;
  }
`;

export const ReplyLink = styled.a`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const NoDataContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;

  padding: 48px 24px;
  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};

  span {
    color: ${colors.onSurfaceVariant};
    font-size: 13px;
  }
`;
